/**
 * SoundService 상위에서 단어 소리를 연속 재새하는 것을 제어하는 서비스
 */
import { Injectable } from '@angular/core';
import {BehaviorSubject, Observable, Subject, Subscription, throwError, timer} from 'rxjs';
import {delay, filter, finalize} from 'rxjs/operators';

import {GUKEO_API_URL1, MAX_EXAMPLE_VOICE} from '../../defines/protocol';
import {PLAY_VOICE_DELAY, VOICE_INTERVAL, WORD_VOICE_REPLAY_COUNT} from '../../defines/client.const';
import {IsContainHttp, IsEmptyString, IsEmptyVar} from '../../defines/common.func';
import {SimpleWord} from '../../models/simple-word.model';
import {Word} from '../../models/word.model';
import {AudioService} from '../audio/audio.service';
import {Homonym} from '../../models/homonym.model';


@Injectable({
  providedIn: 'root'
})
export class VoiceService {
  private playStream$: Subscription | null = null;
  private waitStream$: Subscription | null = null;

  private activeTask$: Subscription | null = null;
  private waitTask$: Subscription | null = null;

  private playWord$: Subscription | null = null;

  private word: Word | SimpleWord | null = null;
  private noExample: number | null = null;
  private playingExampleId: string | null = null;

  private homonym: Homonym | null = null;

  public get on(): boolean {
    // console.log('VoiceService::on', this.word, this.noExample);
    return this.word !== null || this.playingExampleId !== null || this.homonym !== null;
  }
  public get wordId(): string | null {
    return this.word?.id || null;
  }
  public get exampleId(): string | null {
    return this.playingExampleId;
  }
  public get noEx(): number | null {
    return this.noExample;
  }
  public get homonymId(): string | null {
    return this.homonym?.name || null;
  }


  constructor(
    private audioService: AudioService,
  ) { }

  private resetTask$(): void {
    if (this.activeTask$) {
      this.activeTask$.unsubscribe();
      this.activeTask$ = null;
    }
    if (this.waitTask$) {
      this.waitTask$.unsubscribe();
      this.waitTask$ = null;
    }
  }

  private resetStream$(): void {
    // console.log('resetStream$()', this.playStream$, this.waitStream$);
    if (this.playStream$) {
      this.playStream$.unsubscribe();
      this.playStream$ = null;
    }
    if (this.waitStream$) {
      this.waitStream$.unsubscribe();
      this.waitStream$ = null;
    }
  }

  public PlaySound(word: SimpleWord | Word, exNum: number | null = null, count: number = 1): Observable<number> {
    this.Stop();

    this.word = word;
    this.noExample = exNum;

    const url = exNum === null ? word.wordVoice : (() => {
      const w = word as Word;
      if (w.exampleVoice && w.exampleVoice.length > exNum) {
        return w.exampleVoice[exNum];
      }
      return null;
    })();
    if (IsEmptyVar(url)) {
      console.error('VoiceService::PlaySound() no sound word, exNum', word, exNum);
      return throwError({detail: 'invalid word and exNum'});
    }

    const playSound$ = new Subject<number>();

    this.activeTask$ = timer(PLAY_VOICE_DELAY).subscribe(() => {
      this.activeTask$ = this.playSound(url as string, count).pipe(
        finalize(() => {
          this.word = null;
          this.noExample = null;
        })
      ).subscribe(playSound$);
    });

    return playSound$.asObservable();
  }

  public PlayExample(exampleId: string, exampleVoice: string, count: number = 1): Observable<number> {
    this.Stop();

    if (IsEmptyVar(exampleId) || IsEmptyVar(exampleVoice)) {
      console.error('VoiceService::PlayExample() invalid example voice', exampleId, exampleVoice);
      return throwError({detail: 'invalid example voice'});
    }

    this.playingExampleId = exampleId;

    const playSound$ = new Subject<number>();

    this.activeTask$ = timer(PLAY_VOICE_DELAY).subscribe(() => {
      this.activeTask$ = this.playSound(exampleVoice, count).pipe(
        finalize(() => {
          this.playingExampleId = null;
        })
      ).subscribe(playSound$);
    });

    return playSound$.asObservable();
  }

  private playSound(url: string, count: number): Observable<number> {
    let countPlay = 0;

    const fullUrl = IsContainHttp(url) ? url : GUKEO_API_URL1 + url;
    console.log('playSound() url =', fullUrl, count);

    return new Observable<number>(observer => {
      this.playStream$ = this.audioService.playStream(fullUrl).pipe(
        filter((event: Event) => event.type === 'ended')
      ).subscribe((event: Event) => {
        console.log('playSound()::audioService.playStream ended', count);
        observer.next(++countPlay);

        if (--count > 0) {
          this.waitStream$ = timer(VOICE_INTERVAL).subscribe(() => {
            this.waitStream$ = null;
            this.audioService.play().then();
          });
        }
        else {
          this.audioService.stop();
          this.playStream$ = null;
          observer.complete();
        }
      }, err => observer.error(err));

      return () => this.resetStream$();
    });
  }

  /**
   * 항목들을 순차적으로 재생하는 공통 메서드.
   * playExamples()와 playHomonym()의 공통 로직을 추출한 것.
   *
   * @param totalCount 재생할 항목 수
   * @param getVoiceUrl 인덱스로부터 음성 URL을 반환하는 함수
   * @param onBeforePlay 각 항목 재생 전 상태 설정 콜백
   * @param onComplete 모든 항목 재생 완료 후 상태 정리 콜백
   * @param useDelay BehaviorSubject에 delay(0) 적용 여부 (동기 재귀 방지)
   */
  private playSequential(
    totalCount: number,
    getVoiceUrl: (index: number) => string | null | undefined,
    onBeforePlay: (index: number) => void,
    onComplete: () => void,
    useDelay: boolean = false,
  ): Observable<number> {
    return new Observable<number>(observer => {
      const subject$ = new BehaviorSubject<number>(0);
      const source$ = useDelay ? subject$.pipe(delay(0)) : subject$;

      source$.subscribe((index: number) => {
        if (index < totalCount) {
          onBeforePlay(index);
          const voiceUrl = getVoiceUrl(index);

          if (IsEmptyString(voiceUrl as string)) {
            subject$.next(index + 1);
          }
          else {
            const fullUrl = IsContainHttp(voiceUrl as string) ? voiceUrl as string : GUKEO_API_URL1 + voiceUrl;

            this.playStream$ = this.audioService.playStream(fullUrl).pipe(
              filter((event: Event) => event.type === 'ended'),
              finalize(() => {
                this.playStream$ = null;
              })
            ).subscribe(
              (event: Event) => {
                this.playStream$?.unsubscribe();
                observer.next(index);
                this.waitStream$ = timer(VOICE_INTERVAL).subscribe(() => {
                  this.waitStream$ = null;
                  subject$.next(index + 1);
                });
              },
              (err) => {
                // 재생 오류가 나면 기다리지 않고 바로 다음 항목을 재생한다.
                subject$.next(index + 1);
              }
            );
          }
        }
        else {
          this.audioService.stop();
          observer.complete();
          subject$.complete();
          onComplete();
        }
      });

      return () => this.resetStream$();
    });
  }

  private playExamples(word: Word): Observable<number> {
    this.word = word;
    return this.playSequential(
      MAX_EXAMPLE_VOICE,
      (index) => word.exampleVoice[index],
      (index) => { this.noExample = index; },
      () => { this.word = null; this.noExample = null; },
      true,
    );
  }

  public PlayWord(word: Word): Observable<any> {
    this.Stop();

    const playWord$ = new Subject();

    this.playWord$ = timer(PLAY_VOICE_DELAY).subscribe(() => {
      this.playWord$ = this.playWord(word).subscribe(playWord$);
    });

    return playWord$.asObservable();
  }

  private playWord(word: Word): Observable<any> {
    return new Observable<any>(observer => {

      const playExamples = () => {
        this.activeTask$ = this.playExamples(word).pipe(
          finalize(() => {
            observer.complete();
            this.activeTask$ = null;
            this.word = null;
          })
        ).subscribe(
          count => console.log('voiceService::playExamples count =', count),
          err => console.error('voiceService::playExamples error =', err),
        );
      };

      if (!IsEmptyString(word.wordVoice)) {
        this.word = word;
        this.activeTask$ = this.playSound(word.wordVoice, WORD_VOICE_REPLAY_COUNT).pipe(
          finalize(() => {
            console.log('voiceService::PlaySound complete');

            this.waitTask$ = timer(VOICE_INTERVAL).subscribe(() => {
              this.waitTask$ = null;

              playExamples();
            });
          })
        ).subscribe(
          count => console.log('voiceService::PlaySound count =', count),
          err => console.error('voiceService::PlaySound error =', err)
        );
      }
      else {
        playExamples();
      }

      return () => this.resetTask$();
    });
  }

  public PlayHomonym(homonym: Homonym): Observable<number> {
    console.warn('PlayHomonym()', homonym, this.homonym);
    if (this.homonym && homonym.name === this.homonym.name) {
      return throwError({detail: 'Critical Error: PlayHomonym()'});
    }
    this.Stop();

    this.homonym = homonym;
    return this.playHomonym(homonym).pipe(
      finalize(() => {
        this.homonym = null;
        this.Stop();
      })
    );
  }

  private playHomonym(homonym: Homonym): Observable<number> {
    return this.playSequential(
      homonym.listWord.length,
      (index) => homonym.listWord[index].wordVoice,
      (index) => { this.word = homonym.listWord[index]; },
      () => { this.word = null; },
    );
  }

  public Stop(): void {
    if (this.playWord$) {
      this.playWord$.unsubscribe();
      this.playWord$ = null;
    }

    if (this.activeTask$) {
      this.activeTask$.unsubscribe();
      this.activeTask$ = null;
    }

    this.resetStream$();
    this.resetTask$();

    this.word = null;
    this.noExample = null;
    this.playingExampleId = null;
    this.homonym = null;
  }

  public HasVoice(word: Word): boolean {
    return !IsEmptyString(word.wordVoice) && word.exampleVoice?.some(v => !IsEmptyString(v));
  }
}
